<template lang="pug">
  label.p-checkbox.p-checkbox__label(:class="containerClass" :style="containerStyle")
    input.p-checkbox__input(
      v-bind="inputAttributes",
      type="checkbox",
      v-model="state"
      :disabled="disabled || loading || readonly",
      :class="classInput"
      @keyup.enter="state = !state"
      readonly
      v-on="inputListeners"
    )
    .p-checkbox__indicator
    .icone(v-if="$slots.icone")
      slot(name="icone")
    span.p-checkbox__conteudo(:class="classLabel")
      span.p-checkbox__texto
        | {{ label }}
        .icon-info.icone-chapado-info.color-info(
          v-if="infoIcon"
          :title="infoIconTitle"
          :style="`right: ${infoIconPosition}`"
        )
      span.p-checkbox__info-inferior(v-if="infoInferior") {{ infoInferior }}
</template>

<script>
import { handlerUseStateVmodel } from '../mixins/handlerUseStateVmodel'
import {
  extrairAtributosDeControle,
  extrairClasseDoContainer,
  extrairEstiloDoContainer,
  extrairListenersDosAttrs
} from '../mixins/localListenersAndAttributes'

export default {
  name: 'PCheckbox',
  inheritAttrs: false,
  emits: ['update:modelValue', 'change'],
  mixins: [handlerUseStateVmodel()],
  props: {
    label: { type: String, required: true },
    infoIcon: { type: Boolean, required: false, default: false },
    infoIconTitle: { type: String, required: false, default: '' },
    infoIconPosition: { type: String, require: false, default: '-30px' },
    infoInferior: { type: String, required: false, default: '' },
    disabled: Boolean,
    loading: Boolean,
    readonly: Boolean
  },
  computed: {
    containerClass () {
      return extrairClasseDoContainer(this.$attrs)
    },
    containerStyle () {
      return extrairEstiloDoContainer(this.$attrs)
    },
    inputListeners () {
      return extrairListenersDosAttrs(this.$attrs)
    },
    inputAttributes () {
      return extrairAtributosDeControle(this.$attrs, ['type', 'disabled'])
    },
    classLabel () {
      return {
        'p-checkbox__label--loading': this.loading,
        'p-checkbox__label--disabled': this.disabled,
        'p-checkbox__label--readonly': this.readonly
      }
    },
    classInput () {
      return {
        'p-checkbox__input--loading': this.loading,
        'p-checkbox__input--disabled': this.disabled
      }
    }
  }
}
</script>

<style scoped>

.p-checkbox {
  margin-bottom: 5px;
  padding-top: 2px;
  font-size: 14px;
  line-height: 1.35;
  color: var(--texto-forte);
  font-family: var(--texto-tipografia, var(--front-font));
  cursor: pointer;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  display: grid;
  align-items: center;
  grid-template-columns: 22px auto 1fr;
  grid-template-rows: auto;
  align-items: center;
  grid-template-areas:
    "indicator icone label";
}

.p-checkbox__conteudo {
  grid-area: label;
  display: flex;
  flex-direction: column;
  gap: var(--espacamento-4);
}

.p-checkbox__texto {
  position: relative;
}

.p-checkbox__info-inferior {
  color: var(--input-cor-habilitado-info-icone);
  font-size: var(--texto-legenda-pp-tamanho);
  font-weight: var(--texto-legenda-pp-peso);
  line-height: var(--texto-legenda-pp-entrelinha);
}
.icon-info.icone-chapado-info.color-info {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
}
.p-checkbox .p-checkbox__input {
  position: fixed;
  z-index: -1;
  opacity: 0;
}

.p-checkbox:hover .p-checkbox__input:not([disabled]) ~ .p-checkbox__indicator {
  background: var(--superficie-cartao);
  opacity: .8;
}
.p-checkbox:hover .p-checkbox__input:not([disabled]) ~ span,
.p-checkbox__input:focus ~ span  {
  color: var(--color-primary);
  font-weight: 500;
}

.p-checkbox:hover .p-checkbox__input ~ .p-checkbox__indicator,
.p-checkbox .p-checkbox__input:focus ~ .p-checkbox__indicator {
  background: var(--superficie-cartao);
}

.p-checkbox .p-checkbox__input:checked ~ .p-checkbox__indicator {
  background: var(--superficie-cartao);
}

.p-checkbox:hover .p-checkbox__input:not([disabled]):checked ~ .p-checkbox__indicator,
.p-checkbox .p-checkbox__input:checked:focus ~ .p-checkbox__indicator {
  background: var(--superficie-cartao);
}

.p-checkbox .p-checkbox__input:disabled ~ .p-checkbox__indicator {
  background: var(--superficie-cartao);
  opacity: .4;
  pointer-events: none;
}

.p-checkbox__label .p-checkbox__input:disabled ~ .p-checkbox__indicator:after {
  border-color: var(--color-default-disabled);
}

.p-checkbox__label .p-checkbox__input:disabled ~ .p-checkbox__indicator {
  border-color: var(--color-default-disabled);
}

.p-checkbox__label--disabled {
  cursor: auto;
  color: var(--texto-suave);
}

.p-checkbox__label--readonly {
  cursor: auto;
  color: var(--color-default);
}

.p-checkbox__label--loading {
  color: transparent;
  background-color: var(--color-default-disabled);
}

.p-checkbox__indicator {
  border: 1px solid var(--input-cor-habilitado-linha-default, var(--borda));
  border-radius: 2px;
  width: 11px;
  height: 11px;
  display: flex;
  position: relative;
  cursor: pointer;
  margin-right: 5px;
}
.p-checkbox__indicator:after, .p-checkbox__indicator:before  {
  position: absolute;
  content: ' ';
  display: block;
  width: 0;
  height: 0;
  left: 1px;
  top: 5px;
  -webkit-transform: rotate(225deg) rotateY(180deg);
          transform: rotate(225deg) rotateY(180deg);
  -webkit-transform-origin: 0% 0%;
          transform-origin: 0% 0%;
}
.p-checkbox__label .p-checkbox__indicator:before {
  width: 5px;
  box-shadow: inset -2px 0 0 0 var(--color-primary);
  transition: height .15s ease;
}
.p-checkbox__label .p-checkbox__indicator:after {
  height: 8px;
  box-shadow: inset 0 2px 0 0 var(--color-primary);
  transition: width .15s ease .15s;
}
.p-checkbox .p-checkbox__input:checked + .p-checkbox__indicator:before {
  height: 17px;
  transition: height .15s ease .15s;
}
.p-checkbox .p-checkbox__input:checked + .p-checkbox__indicator:after {
  width: 5px;
  transition: width .15s ease;
}

</style>
